import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/auth';
import { StatusCounts } from '@/components/admin/dashboard/StatusCounts';
import { DashboardOrders } from '@/components/admin/dashboard/DashboardOrders';
import { ClientsPanel } from '@/components/admin/dashboard/ClientsPanel';
import { QuickActions, HubLinks } from '@/components/admin/dashboard/QuickActions';
import { WebsiteRequests } from '@/components/admin/dashboard/WebsiteRequests';
import { AlertCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const greeting = () => {
  const h = Number(new Date().toLocaleString('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/Phoenix' }));
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

export default function AdminDashboard() {
  const { isAdmin, user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    authFetch(`${API_BASE}/api/admin/dashboard`)
      .then(async (r) => { if (!r.ok) throw new Error(`Couldn't load dashboard (${r.status})`); setData(await r.json()); })
      .catch((e) => setError(e.message));
  }, [isAdmin]);

  if (!isAdmin) return <Navigate to="/orders" replace />;
  const loading = !data && !error;
  const firstName = (user?.full_name || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-[#050816]" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 pb-16 space-y-8">
        <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#C6A14A]">RC Studios · Admin</p>
            <h1 className="text-4xl sm:text-5xl font-light text-white tracking-wide mt-2" data-testid="admin-dashboard-title">
              {greeting()}{firstName ? `, ${firstName}` : ''}
            </h1>
            <p className="text-[#E8E4DE]/50 mt-2 text-sm">
              {loading ? 'Loading your day…' : data ? `${data.total_orders} orders in the pipeline` : ''}
            </p>
          </div>
          <HubLinks />
        </header>

        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg" data-testid="admin-dashboard-error">
            <AlertCircle className="h-4 w-4 shrink-0" />{error}
          </div>
        )}

        <StatusCounts counts={data?.counts} loading={loading} />
        <QuickActions />
        <WebsiteRequests />
        <DashboardOrders orders={data?.orders || []} loading={loading} />
        <ClientsPanel recentClients={data?.recent_clients || []} />
      </div>
    </div>
  );
}
